// App.js

import { useAuth } from "react-oidc-context";
import { useEffect, useState, useCallback } from "react";
import "./App.css";
import VNav from "./components/VNav";
import MandateTable from "./components/MandateTable";

function App() {
  const auth = useAuth();
  const [mandatesData, setMandatesData] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const signOutRedirect = () => {
    const clientId = "5f7ndohlnkhqu05p6j9biikjia";
    const logoutUri = "https://app.langpartner.ch";
    const cognitoDomain = "https://eu-central-1gdlxxfxev.auth.eu-central-1.amazoncognito.com";
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  };

  // Memoize fetchMandateData
  const fetchMandateData = useCallback(async () => {
    if (!auth.isAuthenticated || isFetching) return;

    setIsFetching(true);
    setFetchError(null);
    try {
      const response = await fetch(
        "https://fhu83jnvn8.execute-api.eu-central-1.amazonaws.com/PROD/getMandateList",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.user?.id_token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          // Token likely expired; trigger re-authentication
          auth.signinRedirect();
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setMandatesData(data);
    } catch (error) {
      console.error("Failed to fetch mandate data:", error);
      setFetchError(error.message);
    } finally {
      setIsFetching(false);
    }
  }, [auth.isAuthenticated, auth.user?.access_token, isFetching]);

  // Fetch data when authenticated
  useEffect(() => {
    if (auth.isAuthenticated && !isFetching && !mandatesData && !fetchError) {
      fetchMandateData();
    }
  }, [auth.isAuthenticated, fetchMandateData, mandatesData, fetchError]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!auth.isLoading && !auth.error && !auth.isAuthenticated) {
      auth.signinRedirect();
    }
  }, [auth.isLoading, auth.error, auth.isAuthenticated]);

  if (auth.isLoading) {
    return <div>Loading...</div>;
  }

  if (auth.error) {
    return (
      <div>
        <p>Encountering error: {auth.error.message}</p>
        <button onClick={() => auth.signinRedirect()}>Try signing in again</button>
      </div>
    );
  }

  if (auth.isAuthenticated) {
    return (
      <div className="">
        <div className="flex columns-2 mx-10 gap-4 min-h-screen">
          <div className="w-40 flex-none ml-10 my-auto">
            <VNav
              email={auth.user?.profile.email}
              onSignOut={() => auth.removeUser()}
              onSignOutRedirect={signOutRedirect}
            />
          </div>
          <div className="flex-1 my-auto">
            <MandateTable mandatesData={mandatesData} idToken={auth.user?.id_token} />
            {isFetching && <p>Loading mandates...</p>}
            {fetchError && (
              <div>
                <p>Error fetching mandates: {fetchError}</p>
                <button onClick={fetchMandateData}>Retry</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return <div>Redirecting to login...</div>;
}

export default App;